/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  PageLayout,
  TableSkeleton,
  DriverModal,
  EmptyState,
  DriversList,
  emptyDriverIllustration,
} from "@components";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useCache } from "../../context/CacheContext";
import { useWorkspaceData } from "@data";
import { useTranslation } from "react-i18next";
import { IDriver } from "../../types";
import { Driver, Company, Vehicle } from "../../models";
import { useDrivers } from "../../hooks";

export const DriversPage: FC = function () {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isUpsertModalOpen, openUpsertModal] = useState(false);
  const [driverData, setDriverData] = useState(Driver.default());
  const [workspaceCompanies, setWorkspaceCompanies] = useState([] as Company[]);
  const [vehicleList, setVehicleList] = useState([] as Vehicle[]);

  const { updateCacheKey } = useCache();
  const { activeWorkspace } = useWorkspace();
  // Drivers, companies and vehicles come from the centrally-loaded store.
  const { drivers, companies, vehicles, loading } = useWorkspaceData();
  // Reuse the shared React Query key. Mounting this page refreshes stale driver
  // data once, while request deduplication prevents a duplicate in-flight call.
  // We deliberately avoid polling the full driver resource from this table.
  const pageDriversQuery = useDrivers();
  const displayedDrivers = pageDriversQuery.data ?? drivers;
  const { t } = useTranslation(["common", "user", "driver"]);

  const isQueryLoading = useMemo(
    () =>
      (loading.drivers && !pageDriversQuery.data) ||
      loading.companies ||
      loading.vehicles,
    [
      loading.drivers,
      loading.companies,
      loading.vehicles,
      pageDriversQuery.data,
    ],
  );

  // Check for 'new' query parameter to open driver creation modal.
  // A driver only needs a company (guaranteed after initial setup); a vehicle
  // is optional and can be linked later, so we no longer gate this on vehicles.
  useEffect(() => {
    const newParam = searchParams.get("new");
    if (newParam === "true" && !isQueryLoading) {
      setDriverData(Driver.default());
      openUpsertModal(true);
    }
  }, [searchParams, isQueryLoading]);

  // Keep previously shared split-view URLs working on the dedicated page.
  useEffect(() => {
    const email = searchParams.get("driver");
    const driver = displayedDrivers.find((item) => item.email === email);
    if (driver?.id) {
      navigate(`/resources/drivers/${encodeURIComponent(driver.id)}`, {
        replace: true,
      });
    }
  }, [searchParams, displayedDrivers, navigate]);

  useEffect(() => {
    if (!isQueryLoading) {
      setWorkspaceCompanies(companies);
    }
  }, [isQueryLoading, companies]);

  useEffect(() => {
    if (!isQueryLoading) {
      setVehicleList(vehicles);
    }
  }, [isQueryLoading, vehicles]);

  return (
    <PageLayout>
      {isQueryLoading ? (
        <TableSkeleton />
      ) : displayedDrivers.length === 0 ? (
        <EmptyState
          icon={emptyDriverIllustration}
          title={t("driver:empty_state.title")}
          description={t("driver:empty_state.description")}
          buttonText={t("driver:empty_state.button_text")}
          buttonClick={() => {
            setDriverData(Driver.default());
            openUpsertModal(true);
          }}
        />
      ) : (
        <DriversList
          drivers={displayedDrivers}
          isLoading={isQueryLoading}
          createOrUpdate={(formData: Driver) => {
            setDriverData(formData);
            openUpsertModal(true);
          }}
        />
      )}

      <DriverModal
        data={driverData}
        existingDrivers={displayedDrivers}
        companies={workspaceCompanies}
        vehicles={vehicleList}
        isShowing={isUpsertModalOpen}
        onConfirm={async (formData: IDriver) => {
          // Helper function to capitalize the first letter of First name and Last name
          const capitalizeFirstLetter = (name: string) =>
            name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

          // Ensure email is lowercase and names are capitalized correctly
          const updatedFormData = {
            ...formData,
            email: formData.email.toLowerCase(), // Convert email to lowercase
            firstName: capitalizeFirstLetter(formData.firstName), // Capitalize first letter of the first name
            lastName: capitalizeFirstLetter(formData.lastName), // Capitalize first letter of the last name
          };

          const isNewDriver = Driver.isNew(driverData);
          if (isNewDriver) {
            const created = await Driver.create(
              activeWorkspace?.workspaceId ?? "",
              updatedFormData,
            ); // Create the new driver with updated data
            if (!created) return false;
          } else {
            try {
              await Driver.update(driverData, updatedFormData); // Update the existing driver with updated data
            } catch (error) {
              console.error("Updating driver failed:", error);
              return false;
            }
          }

          openUpsertModal(false); // Close the modal
          updateCacheKey(); // Refresh the cache
          // Remove query parameter when modal closes
          if (searchParams.get("new") === "true") {
            navigate(window.location.pathname, { replace: true });
          }
          return true;
        }}
        onCancel={() => {
          openUpsertModal(false);
          // Remove query parameter when modal closes
          if (searchParams.get("new") === "true") {
            navigate(window.location.pathname, { replace: true });
          }
        }}
      />
    </PageLayout>
  );
};
